// Copyright (c) Docugami, Inc. All rights reserved.

import { DirectionalHint, IDragDropEvents, IPage, Shimmer, Stack, TooltipHost, TooltipOverflowMode } from "@fluentui/react";
import { DocSetStatus, useDocSetStatus } from "components/cluster_management/DocSetStatus";
import { DgFlexRow } from "components/common/DgCommonStyledComponents";
import { userFontFamily } from "components/common/Fonts";
import { getDirectoryPath } from "components/file_management/file_helpers";
import { DocumentCommandBar } from "components/file_management/presentation/DocumentCommandBar";
import { IDocumentPageViewItem, ListViewColumns } from "components/file_management/presentation/DocumentListView";
import { documentToViewItem, DocumentViewer, DocumentViewerProps, getSortableDocumentFieldFromViewItemField, IDocumentViewControls, IDocumentViewItem } from "components/file_management/presentation/DocumentViewer";
import { useFileUploader } from "components/file_management/presentation/FileUploader";
import { useDocumentsCommands } from "components/file_management/presentation/useDocumentsCommands";
import { DgPill } from "components/homepage/PreviewPill";
import { primaryTheme, primaryThemeSettings } from "components/shell/presentation/AppStyles";
import { getDocumentStatusInfo } from "filemanager/FileStatus";
import { useFeatureFlag } from "hooks/FeatureFlags";
import { useFilePreview } from "hooks/FilePreview";
import { useGetDocumentsFilters } from "hooks/GetDocumentsFilters";
import { useAppSelector } from "hooks/redux";
import { useVirtualizedDocsetDocuments } from "hooks/VirtualizedDocsetDocuments";
import { ClusterInfo } from "networking/api";
import { useEffect, useMemo, useRef, useState } from "react";
import { FeatureFlags } from "store/featureFlags/types";
import { selectDocSetMinFiles } from "store/organization/organizationSlice";
import { getClusterInfoById, getIsDocSetInstalling } from "store/state_helpers";
import styled from "styled-components";
import { DocsetFiltersCommandBar } from "./DocsetFilterCommandBar";
import { DocSetInfo } from "./DocSetInfo";
import { DocSetStatusIndicator } from "./DocSetStatusIndicator";
import { DocSetWorkPathExplorerView } from "./DocSetWorkPathExplorerView";
import { DocumentCountInfo } from "./DocumentCountInfo";
import { FilterIconButton } from "./ExplorerFilterIconButton";
import { InfoItemFooter } from "./ExplorerStyles";
import { ExplorerView } from "./ExplorerView";
import { InstallingSampleDocSetsCoachmark } from "./InstallingSampleDocSetsCoachmark";
import { SelectionCountButton } from "./SelectionCountButton";

const TitleTextContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    overflow: hidden;
`;

const DocSetTitle = styled.span`
    font-size: 24px;
    font-family: ${userFontFamily};
    font-weight: 600;
    color: ${primaryThemeSettings.palette.black};
    white-space: nowrap;
`;

const ExplorerHeader = styled(DgFlexRow)`
    justify-content: flex-start;
    align-items: center;
    min-width: 0;
    gap: 5px;
    .ms-TooltipHost {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

const StyledStack = styled(Stack)`
    min-width: 0;
`;

const InfoContainer = styled(InfoItemFooter)`
    >div:first-child {
        padding-left: 0;
    }
`;

const StyledDocSetWorkPathExplorerView = styled(DocSetWorkPathExplorerView)`
    flex: 1;
    max-height: 33%;
`;

const StyledExplorerView = styled(ExplorerView)`
    flex: 2;
`;

const ContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
`;

const AbsoluteContainer = styled.div`
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
`;

const SelectionButtonRoot = styled.span`
    margin-right: 0;
    margin-left: auto;
`;

interface DocSetExplorerViewProps {
    telemetryId: string;
    docSetId: string;
}

const defaultPageSize = 10;
export const DocSetExplorerView = (props: DocSetExplorerViewProps): JSX.Element => {
    const [selectedItems, setSelectedItems] = useState<string[]>([]);
    const [showFilters, setShowFilters] = useState(false);
    const [pageOffsets, setPageOffsets] = useState<number[]>([0]);

    const docSet = useAppSelector(state => getClusterInfoById(state, props.docSetId));
    const docSetDocThreshold = useAppSelector(selectDocSetMinFiles);

    const showWorkPathList = useFeatureFlag(FeatureFlags.ExperimentalFeatures);

    const viewRef = useRef<IDocumentViewControls>(null);
    const columnFilter: ListViewColumns = ListViewColumns.Icon | ListViewColumns.Name | ListViewColumns.DocStatus | ListViewColumns.AddedOn | ListViewColumns.Size;

    const filtersProps = useGetDocumentsFilters();
    const { setSortState } = filtersProps;
    const { isLoading, totalCount, items } = useVirtualizedDocsetDocuments(props.docSetId, defaultPageSize, filtersProps.filters, pageOffsets);

    const { isLoading: isLoadingStatus, info: docSetStatusInfo } = useDocSetStatus(props.docSetId);
    const isInstalling = useAppSelector(state => getIsDocSetInstalling(state, props.docSetId)) || docSetStatusInfo?.status === DocSetStatus.Copying;

    const [, dropZone] = useFileUploader('', undefined, undefined, props.docSetId);

    const documentsCommands = useDocumentsCommands({ selectedItems, docSet })

    const documentViewItems: IDocumentPageViewItem[] = useMemo(() =>
        items.map(document => !!document ? documentToViewItem(document, getDocumentStatusInfo(document, docSet, docSetDocThreshold)) : document)
        , [items, docSet, docSetDocThreshold])

    const documentIds = useMemo(() => items.filter(x => !!x).map(x => x!.folderId), [items]);
    const { filePreview, setPreviewFile, canPreview, previewCommands, previewOverlays, closeFilePreview } = useFilePreview(documentIds);

    const documentViewProps: DocumentViewerProps = {
        items: documentViewItems,
        isLoading: isLoading,
        selectedItems: selectedItems,
        disableSelectAll: true,
        filter: columnFilter,
        onSelectedItemsChanged: items => setSelectedItems(items),
        actions: {
            name: item => setPreviewFile(item.folderId)
        },
        validateActions: {
            name: item => canPreview(item.folderId)
        },
        sortableColumns: ['name'],
        onSort: (field, descending) => {
            const sortField = getSortableDocumentFieldFromViewItemField(field ?? '');
            setSortState(!!sortField ? { field: sortField, descending: descending } : undefined)
        },
        listProps: {
            onPageAdded: (page: IPage) => {
                const offset = page.startIndex;
                if (pageOffsets.includes(offset)) {
                    return;
                }

                setPageOffsets(x => [...x, offset]);
            },
            onPageRemoved: (page: IPage) => {
                const offset = page.startIndex;
                if (!pageOffsets.includes(offset) || offset === 0) {
                    return;
                }

                setPageOffsets(x => x.filter(x => x !== offset));
            },
            getPageSpecification: () => ({ itemCount: defaultPageSize })
        }
    };

    useEffect(() => {
        closeFilePreview();
        setSelectedItems([]);
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.docSetId]);

    useEffect(() => {
        if (!filePreview) {
            viewRef.current?.selectItems(selectedItems);
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [filePreview]);

    let desiredCommands = documentsCommands

    if (!!filePreview) {
        desiredCommands = { commands: previewCommands, overflowCommands: [], overlays: previewOverlays }
    }

    if (isInstalling) {
        return (
            <InstallingDocSetExplorerView docSet={docSet} telemetryId={props.telemetryId} />
        );
    }

    const isLegacySampleDocset = !!docSet.isDocugamiCluster && !docSet.sampleCatalogItemId;
    const header = (
        <StyledStack>
            <ExplorerHeader>
                <TitleTextContainer>
                    <TooltipHost content={docSet.label + ' Documents'} overflowMode={TooltipOverflowMode.Self}><DocSetTitle>{showWorkPathList ? 'Documents' : docSet.label}</DocSetTitle></TooltipHost>
                    {isLegacySampleDocset && <LegacyDocSetPill />}
                </TitleTextContainer>
                <DocumentCommandBar commands={desiredCommands.commands} overflow={desiredCommands.overflowCommands} />
            </ExplorerHeader>
            <InfoContainer>
                <Shimmer isDataLoaded={!isLoading} width={"50px"}>
                    {!!totalCount && <DocumentCountInfo count={totalCount} countName={'Doc'} />}
                </Shimmer>
                {!!docSetStatusInfo && <DocSetInfo docSetId={props.docSetId} info={docSetStatusInfo} />}
                <FilterIconButton disabled={!!filePreview || totalCount === 0} onClick={() => setShowFilters(v => !v)} />
                {!!selectedItems.length && !filePreview && (
                    <SelectionButtonRoot>
                        <SelectionCountButton
                            count={selectedItems.length}
                            onClear={() => viewRef.current?.clearSelection()} />
                    </SelectionButtonRoot>
                )}
            </InfoContainer>
            {
                !isLoadingStatus && !!docSetStatusInfo && (
                    <DocSetStatusIndicator
                        docSetId={docSet.id!}
                        hidden={!!filePreview || docSetStatusInfo?.status === DocSetStatus.Ready || docSet.isDocugamiCluster}
                        status={docSetStatusInfo?.status} />
                )
            }
        </StyledStack>
    );

    const dragDropEvents: IDragDropEvents = {
        canDrop: () => true,
        onDragEnter: (item, event) => {
            const isDroppable = !!event && !!item && !item.size;
            if (isDroppable) {
                event?.stopPropagation();
                event?.preventDefault();
                return "file-system-drag-enter";
            }

            return "";
        },
        onDrop: (item?: IDocumentViewItem, event?: DragEvent | undefined) => {
            const isDroppable = !!item && !item.size;
            if (!isDroppable || !event?.dataTransfer) {
                return;
            }

            event.stopPropagation();
            event.preventDefault();
            const rootPath = !!item ? getDirectoryPath(item.id) : '';
            dropZone.onDataTransfer(event.dataTransfer, rootPath)
        }
    }

    const content = (
        <ContentContainer>
            <DocsetFiltersCommandBar hidden={!showFilters} onDismiss={() => setShowFilters(false)} {...filtersProps} />
            {!!filePreview ? <AbsoluteContainer>{filePreview}</AbsoluteContainer> : <DocumentViewer dragDropEvents={dragDropEvents} ref={viewRef} {...documentViewProps} />}
        </ContentContainer>
    )

    return (
        <Stack styles={{
            root: {
                height: '100%',
                alignItems: 'center',
                paddingTop: 10
            }
        }}
            onDragOver={dropZone.onDragOver}
            onDrop={dropZone.onDrop}
            onDragEnter={dropZone.onDragEnter} >
            {
                showWorkPathList &&
                    docSetStatusInfo?.status === DocSetStatus.Ready &&
                    !!docSet.clusterId &&
                    !filePreview ?
                    <>
                        <TooltipHost content={docSet.label} overflowMode={TooltipOverflowMode.Self}><DocSetTitle>{docSet.label}</DocSetTitle></TooltipHost>
                        <StyledDocSetWorkPathExplorerView
                            docSet={docSet}
                        />
                    </>
                    :
                    null
            }
            <StyledExplorerView
                header={header}
                content={content}
                overlays={desiredCommands.overlays}
                footer={null}
                telemetryId={props.telemetryId} />
        </Stack>
    );
}

const LegacyDocSetPill = (): JSX.Element => {
    return (
        <DgPill fontSize={12} color={primaryTheme.palette.neutralSecondaryAlt}>
            <TooltipHost
                content={<><p><b>This is a legacy doc set.</b></p>You can still view all documents within the set, and create or modify associated projects. To get new sample doc sets, <a href='/my-content/BrowseSamples'>browse the sample catalog</a> within My Content, and install doc sets to your workspace!</>}
                directionalHint={DirectionalHint.bottomCenter}>
                Legacy Doc Set
            </TooltipHost>
        </DgPill>
    )
}

const InstallingDocSetExplorerView = (props: { docSet: ClusterInfo, telemetryId: string }): JSX.Element => {
    const header = (
        <ExplorerHeader>
            <TooltipHost content={props.docSet.label}><DocSetTitle>{props.docSet.label}</DocSetTitle></TooltipHost>
        </ExplorerHeader>
    );

    return (
        <StyledExplorerView
            header={header}
            content={<></>}
            footer={null}
            overlays={[<InstallingSampleDocSetsCoachmark docSetName={props.docSet.label} />]}
            telemetryId={props.telemetryId} />
    );
}
